import { UppFloorPlanVertex } from '../../floor-plan-element';
import type { FloorPlanAreaSourceDrawVertexItem } from '../floor-plan-wire-types';
import type { UppKonvaShape } from '../floor-plan-wire-types';
import { elementOptsFromLabels } from './shape-bridge-helpers';
import type { FloorPlanShapeBridge, SourceToWireInput } from './shape-bridge-types';

export function vertexRowPolygon(v: FloorPlanAreaSourceDrawVertexItem): string {
  const a = typeof v.poligon === 'string' ? v.poligon.trim() : '';
  const b = typeof v.polygon === 'string' ? v.polygon.trim() : '';
  const p = a.length > 0 ? a : b.length > 0 ? b : '';
  return p.length > 0 ? p : 'default';
}

function vertexRowClosed(v: FloorPlanAreaSourceDrawVertexItem): boolean {
  return v.closed === true;
}

function adapterRowLabels(row: UppKonvaShape): {
  label?: string;
  sublabel?: string;
  zIndex?: number;
  custom?: unknown;
} {
  const o: { label?: string; sublabel?: string; zIndex?: number; custom?: unknown } = {};
  if ('label' in row && row.label !== undefined) {
    o.label = row.label;
  }
  if ('sublabel' in row && row.sublabel !== undefined) {
    o.sublabel = row.sublabel;
  }
  if ('zIndex' in row && row.zIndex !== undefined) {
    o.zIndex = row.zIndex;
  }
  if (Object.prototype.hasOwnProperty.call(row, 'custom')) {
    o.custom = (row as { custom?: unknown }).custom;
  }
  return o;
}

export const vertexShapeBridge: FloorPlanShapeBridge = {
  kind: 'VERTEX',
  wireType: 'VERTEX',
  sourceToWire(i: SourceToWireInput): UppKonvaShape {
    const sort = typeof i.d['sort'] === 'number' && Number.isFinite(i.d['sort']) ? (i.d['sort'] as number) : 0;
    const closed = i.d['closed'] === true;
    const poligon =
      typeof i.d['poligon'] === 'string'
        ? i.d['poligon']
        : typeof i.d['polygon'] === 'string'
          ? (i.d['polygon'] as string)
          : undefined;
    const v: FloorPlanAreaSourceDrawVertexItem = {
      type: 'VERTEX',
      x: i.x,
      y: i.y,
      sort,
      ...(poligon !== undefined && poligon !== '' ? { poligon } : {}),
      ...(closed ? { closed: true } : {}),
      ...(i.id.uuid ? { uuid: i.id.uuid } : {}),
      ...i.lb,
    };
    return { ...v, ...(!i.id.uuid ? { id: i.id.id } : {}) } as UppKonvaShape;
  },
  wireToSource(row: UppKonvaShape) {
    const r = row as FloorPlanAreaSourceDrawVertexItem;
    const uuid = r.uuid !== undefined && r.uuid.trim() !== '' ? r.uuid.trim() : '';
    const legacyId = 'id' in row && typeof (row as { id?: unknown }).id === 'string' ? String((row as { id: string }).id) : '';
    const id = uuid || legacyId || 'shape-VERTEX';
    const base = {
      id,
      x: r.x,
      y: r.y,
      ...('width' in r && typeof r.width === 'number' ? { width: r.width } : {}),
      ...('height' in r && typeof r.height === 'number' ? { height: r.height } : {}),
      ...('rotation' in r && typeof r.rotation === 'number' ? { rotation: r.rotation } : {}),
      ...adapterRowLabels(row),
    };
    return {
      ...base,
      kind: 'VERTEX',
      data: {
        sort: r.sort,
        closed: r.closed === true,
        ...(r.poligon !== undefined ? { poligon: r.poligon } : {}),
      },
    };
  },
  wireToElement(row: UppKonvaShape, uuid: string) {
    const v = row as FloorPlanAreaSourceDrawVertexItem;
    const el = new UppFloorPlanVertex(
      uuid,
      v.x,
      v.y,
      v.sort,
      40,
      40,
      0,
      elementOptsFromLabels(v, false),
      vertexRowPolygon(v),
      vertexRowClosed(v),
    );
    el.zIndex = v.zIndex ?? 0;
    return el;
  },
};
